import React from "react";
import { ArrowRightArrowLeftDuotoneSolid } from "@icons/ArrowRightArrowLeftDuotoneSolid";
import { RectangleVerticalSolidYellowIcon } from "@icons/RectangleVerticalSolidYellowIcon";
import { CardsBlankDuotoneSolid } from "@icons/CardsBlankDuotoneSolid";
import { CircleExclamationRegular } from "@icons/CircleExclamationRegular";
import {
    REGULAR_MATCH_PERIOD,
    SUPPORTED_VALUES,
    TimelineEvent,
} from "../Types";
import { TimelineItem } from "../TimelineItem";
import { RectangleVerticalSolidRed } from "@icons/RectangleVerticalSolidRed";
import { getTimelineTitle } from "./GameTimelineFactory.service";
import { MatchScoreChangeEvent } from "./MatchScoreChangeEvent";
import { usePlayerInfo } from "../../Hooks/usePlayerInfo";
import { VideoAssistantReferee } from "./VideoAssistantReferee";
import { MatchStartedEvent } from "./MatchStartedEvent";
import { MatchEndedEvent } from "./MatchEndedEvent";
import { MatchBreakStartEvent } from "./MatchBreakStartEvent";
import { MatchBreakEndEvent } from "./MatchBreakEndEvent";
import { SxProps } from "@mui/material";
import { FlagPennantClassicSolidIcon } from "@icons/FlagPennantClassicSolidIcon";
import { WhistleClassicSolidIcon } from "@icons/WhistleClassicLight";
import { FlagCheckeredClassicRegularIcon } from "@icons/FlagCheckeredClassicRegularIcon";
import { BullseyeClassicSolidIcon } from "@icons/BullseyeClassicSolidIcon";

export function GameTimelineFactory({
    timelineEvent,
}: {
    timelineEvent: TimelineEvent;
}) {
    const opposite = timelineEvent.teamType === "home";
    const title = getTimelineTitle(timelineEvent);
    const { getPlayerInfo } = usePlayerInfo();

    switch (timelineEvent.type) {
        case SUPPORTED_VALUES.MATCH_STARTED:
            return <MatchStartedEvent />;
        case SUPPORTED_VALUES.MATCH_ENDED:
            return <MatchEndedEvent />;
        case SUPPORTED_VALUES.BREAK_START:
            return <MatchBreakStartEvent />;
        case SUPPORTED_VALUES.PERIOD_START:
            return timelineEvent.periodType ===
                REGULAR_MATCH_PERIOD.SECOND_HALF ? (
                <MatchBreakEndEvent />
            ) : (
                <></>
            );
        case SUPPORTED_VALUES.VIDEO_ASSISTANT_REFEREE:
            return (
                <VideoAssistantReferee
                    timelineEvent={timelineEvent}
                    opposite={opposite}
                    title={title}
                    isOver={false}
                />
            );
        case SUPPORTED_VALUES.VIDEO_ASSISTANT_REFEREE_OVER:
            return (
                <VideoAssistantReferee
                    timelineEvent={timelineEvent}
                    opposite={opposite}
                    title={title}
                    isOver={true}
                />
            );
        case SUPPORTED_VALUES.SCORE_CHANGE:
            return (
                <MatchScoreChangeEvent
                    opposite={opposite}
                    timelineEvent={timelineEvent}
                    title={title}
                />
            );
    }

    let player = getPlayerInfo(timelineEvent);
    let playerName = player.name;
    let Icon: (props: SxProps) => React.JSX.Element = WhistleClassicSolidIcon;
    let fill: string | undefined = "var(--community-color-light-steal-blue)";
    let headline: string, subline: string | undefined;

    let shorterItem = false;

    switch (timelineEvent.type) {
        case SUPPORTED_VALUES.CORNER_KICK:
            Icon = FlagPennantClassicSolidIcon;
            subline = playerName;
            headline = "Eckball";
            shorterItem = true;
            break;
        case SUPPORTED_VALUES.OFFSIDE:
            Icon = FlagCheckeredClassicRegularIcon;
            subline = playerName;
            headline = "Abseits";
            break;
        case SUPPORTED_VALUES.YELLOW_CARD:
            Icon = RectangleVerticalSolidYellowIcon;
            subline = playerName;
            headline = "Gelbe Karte";
            break;
        case SUPPORTED_VALUES.RED_CARD:
            Icon = RectangleVerticalSolidRed;
            subline = playerName;
            headline = "Rote Karte";
            break;
        case SUPPORTED_VALUES.YELLOW_RED_CARDS:
            Icon = CardsBlankDuotoneSolid;
            subline = playerName;
            headline = "Gelbe-Rote Karte";
            break;
        case SUPPORTED_VALUES.SHOT_ON_TARGET:
            Icon = BullseyeClassicSolidIcon;
            subline = playerName;
            headline = "Torschuss";
            shorterItem = true;
            break;
        case SUPPORTED_VALUES.PENALTY_MISSED:
            Icon = CircleExclamationRegular;
            subline = playerName;
            headline = "Elfmeter verschossen";
            break;
        case SUPPORTED_VALUES.SUBSTITUTION: {
            const playerOut = getPlayerInfo(timelineEvent, "substituted_out");
            const playerIn = getPlayerInfo(timelineEvent, "substituted_in");
            if (!playerOut.id || !playerIn.id) {
                return <></>;
            }
            fill = undefined;
            Icon = ArrowRightArrowLeftDuotoneSolid;
            subline = `für ${playerOut.name}`;
            headline = playerIn.name;
            player = getPlayerInfo(timelineEvent, "substituted_in");
            playerName = player.name;
            break;
        }
        case SUPPORTED_VALUES.FREE_KICK:
            subline = playerName;
            headline = "Freistoß";
            shorterItem = true;
            break;
        default:
            return <></>;
    }

    return (
        <TimelineItem
            shorterItem={shorterItem && !playerName}
            opposite={opposite}
            title={title}
            subline={subline}
            playerData={player}
            headline={headline}
            icon={<Icon fill={fill} />}
        />
    );
}
